import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Slider } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { SwitchViewMode } from "../../shared/enums/SwitchViewMode";
import { MachineTonnage } from "../../store/interfaces/MachineTonnage";
import "./Tonnage.scss";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import { action } from "../../store/action";
import { useMarket } from "../../store/hooks/useMarket";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import { useStateCleaner } from "../Products/hooks/useStateCleaner";

interface TonnageProps {
  currentTonnage: number;
  hasSelectedProducts: boolean;
  onClick: React.MouseEventHandler<HTMLElement>;
}

interface TonnageConfigProps {
  defaultValue: number;
  max: number;
  min: number;
  step: number;
}

const defaultMachineTonnage: MachineTonnage = {
  machineModel: undefined,
  switchViewMode: SwitchViewMode.Tonnage,
  tonnage: undefined,
};

const getTonnageConfig = (market: string): TonnageConfigProps => {
  return {
    defaultValue: market === "en-US" ? 3500 : 1.5,
    max: market === "en-US" ? 95000 : 43,
    min: market === "en-US" ? 3500 : 1.5,
    step: market === "en-US" ? 500 : 0.5
  };
};

const Tonnage = ({
  currentTonnage,
  hasSelectedProducts,
  onClick,
}: TonnageProps) => {
  const market = useMarket();
  const dispatch = useDispatch();
  const { clearStateAllProducts } = useStateCleaner();
  const { t } = useTranslation(["machinetonnagestep", "metrics", "popup"]);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  if(currentTonnage && market ==="en-US") {
    currentTonnage = currentTonnage*1000;
  }
  const [tonnageValue, setTonnageValue] = useState<number>(
    currentTonnage ? currentTonnage : market === "en-US" ? 3500 : 1.5,
  );
  const [internalTonnageValue, setInternalTonnageValue] = useState<number>(
    currentTonnage ? currentTonnage : market === "en-US" ? 3500 : 1.5,
  );
  const [newTonnageValue, setNewTonnageValue] = useState<number>(0);
  const tonnageConfig = getTonnageConfig(market);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onKeyUp = (event: any) => {
    const numericValue = Number(event.target.value);

    if (numericValue > tonnageConfig.max) {
      setInternalTonnageValue(tonnageConfig.max);
      setTonnageValue(tonnageConfig.max);
    }

    if (numericValue < tonnageConfig.min) {
      setInternalTonnageValue(tonnageConfig.min);
      setTonnageValue(tonnageConfig.min);
    }
  };

  const onInputChange = (value: string) => {
    setInternalTonnageValue(Number(value));

    if (currentTonnage && hasSelectedProducts) {
      setNewTonnageValue(Number(value));
      setShowDialog(true);
      return;
    }

    changeValue(value);
  };

  const onSliderChangeCommitted = (value: number | number[]) => {
    const tempValue = value || value[0];

    if (currentTonnage && hasSelectedProducts) {
      setNewTonnageValue(tempValue);
      setShowDialog(true);
      return;
    }

    changeValue(value.toString());
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSliderChange = (event: any) => {
    setInternalTonnageValue(event.target.value);
  };

  const onUpDown = (isUp: boolean) => {
    const newValue = getUpDownValue(isUp);

    if (!newValue) {
      return;
    }

    if (currentTonnage && hasSelectedProducts) {
      setInternalTonnageValue(newValue);
      setNewTonnageValue(newValue);
      setShowDialog(true);
      return;
    }

    changeValue(newValue.toString());
    setInternalTonnageValue(newValue);
  };

  const onDialogClose = () => {
    setInternalTonnageValue(tonnageValue);
    setShowDialog(false);
  };

  const onDialogCloseOK = () => {
    changeValue(newTonnageValue.toString());
    clearStateAllProducts();
    setShowDialog(false);
  };

  const getUpDownValue = (isUp: boolean): number => {
    if (isUp && tonnageValue < tonnageConfig.max) {
      return tonnageValue + tonnageConfig.step;
    }

    if (!isUp && tonnageValue > tonnageConfig.min) {
      return tonnageValue - tonnageConfig.step;
    }

    const outOfRange = undefined;
    return outOfRange;
  };

  const changeValue = (value: string) => {
    let numericValue = 0
    if(market === "en-US") {
      numericValue = Number(value)/1000
    }
    else {
      numericValue = Number(value);
    }
    setTonnageValue(Number(numericValue));

    const machineTonnage: MachineTonnage = {
      ...defaultMachineTonnage,
      tonnage: numericValue,
    };
    dispatch(action.saveMachineTonnage(machineTonnage));

    const enableNext = numericValue > 0 ? true : false;
    dispatch(action.saveWizardEnableNext(enableNext));
  };

  return (
    <div className="tonnageContainer">
      <div className="tonnage">
        <div className="tonnageHeader">
          <h2>{t("machinetonnagetexts.choosetonnage")}</h2>
          <div className="tonnageSwitch" onClick={onClick}>
            <span>{t("machinetonnagetexts.switchtomachine")}</span>
            <NavigateNextIcon />
          </div>
        </div>
        <div className="tonnageText">
          {t("machinetonnagetexts.picktonnagetext2")}
        </div>
        <div className="tonnageInfo">
          {t("machinetonnagetexts.choosetonnage")}{" "}
          {market === "en-US" ? t("lb", { ns: "metrics" }) : t("tonnes", { ns: "metrics" })}
        </div>
        <div className="tonnageInputContainer">
          <input
            className="tonnageInput"
            max={tonnageConfig.max}
            min={tonnageConfig.min}
            onChange={(event) => {
              onInputChange(event.target.value);
            }}
            onKeyUp={onKeyUp}
            step={tonnageConfig.step}
            type="number"
            value={internalTonnageValue}
          ></input>
          <div className="inputUpDown">
            <div
              className="inputUp"
              onClick={() => {
                onUpDown(true);
              }}
            >
              <KeyboardArrowUpIcon />
            </div>
            <div
              className="inputDown"
              onClick={() => {
                onUpDown(false);
              }}
            >
              <KeyboardArrowDownIcon />
            </div>
          </div>
        </div>
        <div className="tonnageHelp">
          {t("machinetonnagetexts.tonnagehelpertext")}
        </div>
        <div className="tonnageSlider">
          <Slider
            color="primary"
            defaultValue={tonnageConfig.defaultValue}
            max={tonnageConfig.max}
            min={tonnageConfig.min}
            onChange={onSliderChange}
            onChangeCommitted={(_, v) => onSliderChangeCommitted(v)}
            step={tonnageConfig.step}
            valueLabelDisplay="auto"
            value={internalTonnageValue}
          />
        </div>
      </div>
      <ConfirmDialog
        dialogHeader={t("important", { ns: "popup" })}
        text={t("areyousure", { ns: "popup" })}
        okButtonText={t("clearchoises", { ns: "popup" })}
        cancelButtonText={t("cancel", { ns: "popup" })}
        open={showDialog}
        onClose={onDialogClose}
        onCancelClick={onDialogClose}
        onOkClick={onDialogCloseOK}
      ></ConfirmDialog>
    </div>
  );
};

export default Tonnage;
